import React from 'react';
import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import styles from './app.module.css';

// Публичный сайт — eager (компоненты главной страницы нужны сразу)
import Main from '../../page/main/Main';
import NotFound from '../../page/notfound/NotFound';
import AppHeader from '../appheader/AppHeader';
import SliderContainer from '../../page/slidercontainer/SliderContainer';
import HowToInstall from '../../page/howtoinstall/HowToInstall';
import Faq from '../../page/faq/Faq';
import LatestNews from '../../page/latestnews/LatestNews';
import Community from '../../page/community/Community';
import Footer from '../footer/Footer';
import Advantages from '../../page/advantages/Advantages';
import Scrollbar from '../scrollbar/Scrollbar';
import { TServerStatus } from '../../utils/types';
import { getServerState } from '../../utils/api';
import { UserAuthProvider, UserAuthGuard, useUserAuth } from '../../contexts/UserAuthContext';
import { AdminAuthProvider, AdminAuthGuard } from '../../contexts/AdminAuthContext';
import { ToastProvider } from '../../contexts/ToastContext';
import { useSiteConfig } from '../../contexts/SiteConfigContext';
import ToastContainer from '../toast/ToastContainer';
import ErrorBoundary from '../errorBoundary/ErrorBoundary';
import CookieConsent from '../cookieConsent/CookieConsent';
import SteamLinkRedirect from '../auth/SteamLinkRedirect';
import SupportChatButton from '../supportChat/SupportChatButton';
import { startLogin, startRegister } from '../../utils/oidc';
import { decodeTokenPermissions, USER_TOKEN_KEY } from '../../utils/authTokens';
import { trackPageView } from '../../utils/analytics';
import { firstAccessibleAdminPath } from '../panel/adminNavConfig';
import { runMainPreloader, removePreloader } from '../../utils/preloader';
import { enablePagedScroll, canUsePagedScroll } from '../../utils/pageScroll';
import { startSectionReveal } from '../../utils/sectionReveal';
import PageLoader from '../loader/PageLoader';
import { loadDonate, loadRules, loadPublicPage, loadNewsList, loadNewsArticle, preloadPublicRoutes } from '../../utils/publicRoutes';
import { isSiteRoute } from '../../utils/publicRoutes';
import FullScreenLoader from '../loader/FullScreenLoader';
import { lazyWithRetry } from './lazyWithRetry';
import { panelChunks, startPanelPrefetch } from './routeChunks';

// Базовый URL портала авторизации (SSR-страницы amazing_auth)
const AUTH_PORTAL = process.env.REACT_APP_AUTH_URL ?? 'http://localhost:3002';

// Donate — отдельная страница, загружается лениво
const Donate = lazyWithRetry(loadDonate);
// Rules — публичная страница правил, lazy (зависит от DOMPurify)
const Rules = lazyWithRetry(loadRules);

// Admin — lazy (тяжёлые зависимости: leaflet, @hello-pangea/dnd и т.д.)
const AdminDashboard    = lazyWithRetry(panelChunks['admin/dashboard']);
const AdminChatPage     = lazyWithRetry(panelChunks['admin/chat']);
const AdminTasksPage    = lazyWithRetry(panelChunks['admin/tasks']);
const AdminTicketsPage  = lazyWithRetry(panelChunks['admin/tickets']);
const AdminMap          = lazyWithRetry(panelChunks['admin/map']);
const AdminUsers        = lazyWithRetry(panelChunks['admin/users']);
const AdminUserDetail   = lazyWithRetry(panelChunks['admin/users/:id']);
const AdminRoles        = lazyWithRetry(panelChunks['admin/roles']);
const AdminDocuments    = lazyWithRetry(panelChunks['admin/documents']);
const AdminSettings     = lazyWithRetry(panelChunks['admin/settings']);
const AdminOidcClients  = lazyWithRetry(panelChunks['admin/oidc-clients']);
const AdminRules        = lazyWithRetry(panelChunks['admin/rules']);
const AdminFaq          = lazyWithRetry(panelChunks['admin/faq']);
const AdminLogsShell = lazyWithRetry(panelChunks['admin/logs']);
const AdminLogsSite     = lazyWithRetry(panelChunks['admin/logs/site']);
const AdminLogsAuth     = lazyWithRetry(panelChunks['admin/logs/auth']);
const AdminLogsMoney    = lazyWithRetry(panelChunks['admin/logs/money']);
const AdminLogsVehicles = lazyWithRetry(panelChunks['admin/logs/vehicles']);
const AdminLogsResources = lazyWithRetry(panelChunks['admin/logs/resources']);
const AdminLogsBpla = lazyWithRetry(panelChunks['admin/logs/bpla']);
const AdminLogsMommaHeart = lazyWithRetry(panelChunks['admin/logs/momma-heart']);
const AdminLogsTerritory = lazyWithRetry(panelChunks['admin/logs/territory']);
const AdminLogsEvents = lazyWithRetry(panelChunks['admin/logs/events']);
const AdminLogsKills = lazyWithRetry(panelChunks['admin/logs/kills']);
const AdminLogsComms = lazyWithRetry(panelChunks['admin/logs/comms']);
const AdminLogsCasino = lazyWithRetry(panelChunks['admin/logs/casino']);
const AdminFiles        = lazyWithRetry(panelChunks['admin/files']);
const AdminVehicles     = lazyWithRetry(panelChunks['admin/vehicles']);
const AdminPlayer       = lazyWithRetry(panelChunks['admin/players']);
const AdminFractions    = lazyWithRetry(panelChunks['admin/fractions']);
const AdminFraction     = lazyWithRetry(panelChunks['admin/fraction']);
const AdminPromocodes   = lazyWithRetry(panelChunks['admin/promocodes']);
const AdminFinance      = lazyWithRetry(panelChunks['admin/finance']);
const AdminOnline       = lazyWithRetry(panelChunks['admin/online']);

// Публичная страница скачивания файла из хранилища (доступ по прямой ссылке)
const FileDownloadPage  = lazyWithRetry(() => import('../../page/files/FileDownload'));

// Публичные CMS-страницы (/pages/<slug>, /pages/<категория>/<slug>)
const PublicPage        = lazyWithRetry(loadPublicPage);
const AdminPages        = lazyWithRetry(panelChunks['admin/pages']);

// Новости (публичный раздел + админка) и админка лаунчера
const NewsList          = lazyWithRetry(loadNewsList);
const NewsArticle       = lazyWithRetry(loadNewsArticle);
const AdminNews         = lazyWithRetry(panelChunks['admin/news']);
const AdminLauncher     = lazyWithRetry(panelChunks['admin/launcher']);
const AdminStats        = lazyWithRetry(panelChunks['admin/stats']);

// Справочник кодов ошибок: общий на установщик, лаунчер и сервисы сайта
const AdminErrors       = lazyWithRetry(panelChunks['admin/errors']);

// Инциденты: здоровье компонентов системы и история недоступностей
const AdminIncidents    = lazyWithRetry(panelChunks['admin/incidents']);
const AdminMultiacc     = lazyWithRetry(panelChunks['admin/multiaccounts']);
const AdminBans         = lazyWithRetry(panelChunks['admin/bans']);
const AdminServer       = lazyWithRetry(panelChunks['admin/server']);

// Panel — lazy
const PanelLayout    = lazyWithRetry(panelChunks['layout']);
const MyTickets      = lazyWithRetry(panelChunks['tickets']);
const MyTicketDetail = lazyWithRetry(panelChunks['tickets/:id']);
const NewTicket      = lazyWithRetry(panelChunks['tickets/new']);
const PanelProfile   = lazyWithRetry(panelChunks['profile']);
const PanelTopup     = lazyWithRetry(panelChunks['topup']);
const AccessDeniedPage = lazyWithRetry(panelChunks['access-denied']);
const ConsentPage    = lazyWithRetry(panelChunks['consent']);
const SteamCallback  = lazyWithRetry(panelChunks['steam-callback']);

// Страница проверки мониторинга ошибок (Sentry/GlitchTip)
const SentryTest     = lazyWithRetry(() => import('../../page/debug/SentryTest'));

// OIDC callback (обмен code → токены)
const Callback       = lazyWithRetry(() => import('../../page/auth/Callback'));
// Тихое обновление токена (silent_redirect_uri) — лёгкая страница без UI
const SilentCallback = lazyWithRetry(() => import('../../page/auth/SilentCallback'));

// Простой полноэкранный индикатор ожидания редиректа
const RedirectSpinner: React.FC = () => <FullScreenLoader caption="Перенаправление" />;

// Регистрация/восстановление пароля перенесены на SSR-портал amazing_auth.
// Вход — через OIDC-редирект (PKCE).
const OidcLoginRedirect: React.FC = () => {
  React.useEffect(() => { void startLogin('/panel'); }, []);
  return <RedirectSpinner />;
};
// Регистрация — OIDC-флоу с register=1 (форма регистрации внутри interaction).
const OidcRegisterRedirect: React.FC = () => {
  React.useEffect(() => { void startRegister('/panel'); }, []);
  return <RedirectSpinner />;
};
const ExternalRedirect: React.FC<{ to: string; keepQuery?: boolean }> = ({ to, keepQuery }) => {
  React.useEffect(() => {
    const url = keepQuery ? `${to}${window.location.search}` : to;
    window.location.replace(url);
  }, [to, keepQuery]);
  return <RedirectSpinner />;
};

// /panel/admin → первый доступный по правам раздел (helper попадает в «Задачи»)
const AdminIndexRedirect: React.FC = () => {
  const permissions = React.useMemo(
    () => decodeTokenPermissions(localStorage.getItem(USER_TOKEN_KEY)),
    [],
  );
  return <Navigate to={firstAccessibleAdminPath(permissions)} replace />;
};


// /panel/register: весь онбординг — на SSR-портале amazing_auth (OIDC-native),
// чтобы пользователь всегда получал OIDC-токен (а не legacy).
//  • незалогинен — старт OIDC-регистрации (форма внутри interaction);
//  • email_unverified — повторный OIDC-вход (подтверждение email в interaction);
//  • steam_unlinked — SSR-страница привязки Steam;
//  • active — в панель.
const RegisterRoute: React.FC = () => {
  const { user, loading } = useUserAuth();
  if (loading) return <PageLoader />;
  if (!user) return <OidcRegisterRedirect />;
  if (user.status === 'active') return <Navigate to="/panel" replace />;
  if (user.status === 'steam_unlinked') return <SteamLinkRedirect />;
  // email_unverified — форсируем повторный вход (prompt=login), чтобы portal
  // показал шаг подтверждения email внутри OIDC-interaction и выдал OIDC-токен.
  return <OidcReverifyRedirect />;
};

// Повторный вход с принудительной формой (prompt=login) — для дозавершения
// онбординга (подтверждение email).
const OidcReverifyRedirect: React.FC = () => {
  React.useEffect(() => { void startLogin('/panel', true); }, []);
  return <RedirectSpinner />;
};

const DelayedLoader: React.FC = () => {
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    const timer = window.setTimeout(() => setVisible(true), 300);
    return () => window.clearTimeout(timer);
  }, []);
  return visible ? <PageLoader /> : <div style={{ minHeight: '100vh' }} />;
};

const PublicFallback: React.FC = () => (
  <main className={styles.main}>
    <AppHeader />
    <DelayedLoader />
  </main>
);

const serverStatusDefault: TServerStatus = {
  status: false,
  players: 0
};

// Маршруты которые должны быть без snap-scroll
const NO_SNAP_PATHS = ['/donate', '/panel', '/rules', '/pages', '/news'];

/**
 * Выключённый в настройках раздел не должен открываться даже по прямой ссылке.
 * Состояние hidden оставляет маршрут рабочим — скрыт только пункт в шапке.
 */
const NewsGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { newsSection, ready } = useSiteConfig();
  if (!ready) return <PageLoader />;
  if (newsSection === 'off') return <NotFound />;
  return <>{children}</>;
};

function App() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const isPanel = location.pathname.startsWith('/panel') || isAdmin;
  const isNoSnap = NO_SNAP_PATHS.some(p => location.pathname.startsWith(p));

  const [serverStatus, setServerStatus] = React.useState(serverStatusDefault);

  // Прелоадер из index.html: на главной ждёт шрифты и картинки первого экрана,
  // на остальных страницах убирается сразу
  React.useEffect(() => {
    if (location.pathname === '/') runMainPreloader();
    else removePreloader();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigate = useNavigate();
  // Ссылки на страницы сайта внутри текста (статьи, CMS, правила) приходят
  // обычным HTML — открываем их без перезагрузки, как ссылки шапки
  const onContentLinkClick = (e: React.MouseEvent) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = (e.target as HTMLElement).closest('a');
    if (!a || (a.target && a.target !== '_self') || a.hasAttribute('download')) return;
    const url = new URL(a.href, window.location.href);
    if (url.origin !== window.location.origin) return;
    if (!isSiteRoute(url.pathname)) return;
    if (url.pathname === window.location.pathname && url.hash) return;
    e.preventDefault();
    navigate(url.pathname + url.search + url.hash);
  };
  const allRef = React.useRef<HTMLDivElement>(null);
  React.useLayoutEffect(() => {
    const el = allRef.current;
    if (!el || location.hash) return;
    el.style.scrollBehavior = 'auto';
    el.scrollTop = 0;
    el.style.scrollBehavior = '';
  }, [location.pathname, location.hash]);

  // Главная на ПК листается своей плавной анимацией (см. utils/pageScroll)
  const isHome = location.pathname === '/';
  const [paged, setPaged] = React.useState(false);
  React.useEffect(() => {
    const el = allRef.current;
    if (!el || !isHome || !canUsePagedScroll()) { setPaged(false); return undefined; }
    setPaged(true);
    return enablePagedScroll(el);
  }, [isHome]);

  // Анимация появления экранов главной при прокрутке
  React.useEffect(() => {
    const el = allRef.current;
    if (!el || !isHome) return undefined;
    return startSectionReveal(el);
  }, [isHome]);

  // Разделы сайта подгружаем заранее, пока человек на главной: переход на
  // донат, правила и новости тогда не показывает загрузку
  React.useEffect(() => {
    if (!isPanel) preloadPublicRoutes();
  }, [isPanel]);

  // Кабинет и админка: код разделов скачивается заранее — в простое после
  // входа и при наведении/фокусе на ссылку меню, поэтому переход не ждёт сеть
  React.useEffect(() => (isPanel ? startPanelPrefetch() : undefined), [isPanel]);

  // Просмотры страниц для раздела «Статистика»
  React.useEffect(() => { trackPageView(location.pathname); }, [location.pathname]);

  // useRef предотвращает дублирование fetch-запроса без лишних ре-рендеров
  const serverStatusFetched = React.useRef(false);

  const mainRef = React.useRef(null);
  const advantagesRef = React.useRef(null);
  const sliderContainerRef = React.useRef(null);
  const howToInstallRef = React.useRef(null);

  React.useEffect(() => {
    if (isPanel) return;

    if (!serverStatusFetched.current) {
      serverStatusFetched.current = true;
      getServerState()
        .then((result: TServerStatus) => setServerStatus(result))
        .catch(() => setServerStatus({ status: false, players: 0 }));
    }

  }, [isPanel]);

  // Admin/Panel — единый layout
  if (isPanel) {
    return (
      <ToastProvider>
        <UserAuthProvider key="panel">
          <AdminAuthProvider>
            <ErrorBoundary>
              {/* Внешняя граница — только до загрузки каркаса панели (и для
                  экранов без него). Разделы внутри каркаса ждут своего кода
                  в границе PanelLayout: сайдбар не пропадает, а переходы идут
                  через startTransition (v7_startTransition в index.tsx) —
                  прежний раздел виден, пока не готов следующий. */}
              <React.Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Редиректы со старых /admin/* URL → /panel/admin/* */}
              <Route path="/admin/login"     element={<Navigate to="/panel/login"            replace />} />
              <Route path="/admin"           element={<Navigate to="/panel/admin/dashboard"  replace />} />
              <Route path="/admin/dashboard" element={<Navigate to="/panel/admin/dashboard"  replace />} />
              <Route path="/admin/map"       element={<Navigate to="/panel/admin/map"        replace />} />
              <Route path="/admin/documents" element={<Navigate to="/panel/admin/documents"  replace />} />
              <Route path="/admin/users"     element={<Navigate to="/panel/admin/users"      replace />} />
              <Route path="/admin/users/:id" element={<NavigateAdminUser />} />
              <Route path="/admin/settings"  element={<Navigate to="/panel/admin/settings"   replace />} />
              <Route path="/admin/access"    element={<Navigate to="/panel/admin/roles"      replace />} />
              <Route path="/admin/*"         element={<Navigate to="/panel/admin/dashboard"  replace />} />

              {/* Вход — OIDC-редирект; регистрация/сброс пароля — SSR-портал amazing_auth */}
              <Route path="/panel/login"            element={<OidcLoginRedirect />} />
              <Route path="/panel/register"         element={<RegisterRoute />} />
              <Route path="/panel/forgot-password"  element={<ExternalRedirect to={`${AUTH_PORTAL}/account/forgot-password`} />} />
              <Route path="/panel/reset-password"   element={<ExternalRedirect to={`${AUTH_PORTAL}/account/reset-password`} keepQuery />} />
              {/* Steam OAuth callback — обрабатывает токены из URL */}
              <Route path="/panel/steam-callback"   element={<SteamCallback />} />
              {/* Страница принятия соглашений (без UserAuthGuard — чтобы не зациклить редирект) */}
              <Route path="/panel/consent"          element={<ConsentPage />} />
              {/* Авторизованная зона — с сайдбаром */}
              <Route path="/panel" element={<PanelLayout />}>
                <Route index element={<Navigate to="tickets" replace />} />
                <Route path="tickets"     element={<UserAuthGuard><MyTickets /></UserAuthGuard>} />
                {/* «new» раньше «:id», иначе роутер примет слово за номер обращения */}
                <Route path="tickets/new" element={<UserAuthGuard><NewTicket /></UserAuthGuard>} />
                <Route path="tickets/:id" element={<UserAuthGuard><MyTicketDetail /></UserAuthGuard>} />
                <Route path="profile"     element={<UserAuthGuard><PanelProfile /></UserAuthGuard>} />
                <Route path="topup"       element={<UserAuthGuard><PanelTopup /></UserAuthGuard>} />
                <Route path="access-denied" element={<UserAuthGuard><AccessDeniedPage /></UserAuthGuard>} />
                {/* Маршруты администратора — видны только при наличии роли */}
                <Route path="admin"                element={<AdminIndexRedirect />} />
                <Route path="admin/dashboard"      element={<AdminAuthGuard><AdminDashboard /></AdminAuthGuard>} />
                <Route path="admin/chat"           element={<AdminAuthGuard><AdminChatPage /></AdminAuthGuard>} />
                <Route path="admin/tasks"          element={<AdminAuthGuard><AdminTasksPage /></AdminAuthGuard>} />
                <Route path="admin/tickets"        element={<AdminAuthGuard><AdminTicketsPage /></AdminAuthGuard>} />
                <Route path="admin/map"            element={<AdminAuthGuard><AdminMap /></AdminAuthGuard>} />
                <Route path="admin/documents"      element={<AdminAuthGuard><AdminDocuments /></AdminAuthGuard>} />
                <Route path="admin/users"          element={<AdminAuthGuard><AdminUsers /></AdminAuthGuard>} />
                <Route path="admin/users/:id"      element={<AdminAuthGuard><AdminUserDetail /></AdminAuthGuard>} />
                <Route path="admin/players/:steamId" element={<AdminAuthGuard><AdminPlayer /></AdminAuthGuard>} />
                <Route path="admin/fractions" element={<AdminAuthGuard><AdminFractions /></AdminAuthGuard>} />
                <Route path="admin/fractions/:code" element={<AdminAuthGuard><AdminFraction /></AdminAuthGuard>} />
                <Route path="admin/roles"          element={<AdminAuthGuard><AdminRoles /></AdminAuthGuard>} />
                <Route path="admin/settings"       element={<AdminAuthGuard><AdminSettings /></AdminAuthGuard>} />
                <Route path="admin/oidc-clients"   element={<AdminAuthGuard><AdminOidcClients /></AdminAuthGuard>} />
                <Route path="admin/rules"          element={<AdminAuthGuard><AdminRules /></AdminAuthGuard>} />
                <Route path="admin/faq"            element={<AdminAuthGuard><AdminFaq /></AdminAuthGuard>} />
                {/* Раздел «Логи»: стартовая страница (игрок, период, журналы),
                    каждый журнал — дочерний маршрут со своей страницей. */}
                <Route path="admin/logs" element={<AdminAuthGuard><AdminLogsShell /></AdminAuthGuard>}>
                  <Route path="site"        element={<AdminLogsSite />} />
                  <Route path="auth"        element={<AdminLogsAuth />} />
                  <Route path="money"       element={<AdminLogsMoney />} />
                  <Route path="vehicles"    element={<AdminLogsVehicles />} />
                  <Route path="resources"   element={<AdminLogsResources />} />
                  <Route path="bpla"        element={<AdminLogsBpla />} />
                  <Route path="momma-heart" element={<AdminLogsMommaHeart />} />
                  <Route path="territory"   element={<AdminLogsTerritory />} />
                  <Route path="events"      element={<AdminLogsEvents />} />
                  <Route path="kills"       element={<AdminLogsKills />} />
                  <Route path="comms"       element={<AdminLogsComms />} />
                  <Route path="casino"      element={<AdminLogsCasino />} />
                </Route>
                <Route path="admin/files"          element={<AdminAuthGuard><AdminFiles /></AdminAuthGuard>} />
                <Route path="admin/pages"          element={<AdminAuthGuard><AdminPages /></AdminAuthGuard>} />
                <Route path="admin/news"           element={<AdminAuthGuard><AdminNews /></AdminAuthGuard>} />
                <Route path="admin/launcher"       element={<AdminAuthGuard><AdminLauncher /></AdminAuthGuard>} />
                <Route path="admin/errors"         element={<AdminAuthGuard><AdminErrors /></AdminAuthGuard>} />
                <Route path="admin/incidents"      element={<AdminAuthGuard><AdminIncidents /></AdminAuthGuard>} />
                <Route path="admin/multiaccounts"  element={<AdminAuthGuard><AdminMultiacc /></AdminAuthGuard>} />
                <Route path="admin/bans"           element={<AdminAuthGuard><AdminBans /></AdminAuthGuard>} />
                <Route path="admin/server"         element={<AdminAuthGuard><AdminServer /></AdminAuthGuard>} />
                <Route path="admin/vehicles"       element={<AdminAuthGuard><AdminVehicles /></AdminAuthGuard>} />
                <Route path="admin/promocodes"     element={<AdminAuthGuard><AdminPromocodes /></AdminAuthGuard>} />
                <Route path="admin/finance"        element={<AdminAuthGuard><AdminFinance /></AdminAuthGuard>} />
                <Route path="admin/online"         element={<AdminAuthGuard><AdminOnline /></AdminAuthGuard>} />
                <Route path="admin/stats"          element={<AdminAuthGuard><AdminStats /></AdminAuthGuard>} />
              </Route>
                <Route path="*" element={<Navigate to="/panel/login" replace />} />
              </Routes>
            </React.Suspense>
          </ErrorBoundary>
          <ToastContainer />
          <CookieConsent />
        </AdminAuthProvider>
      </UserAuthProvider>
    </ToastProvider>
    );
  }

  // Публичный сайт
  return (
    <ToastProvider>
    <div ref={allRef} className={`${styles.all} ${isNoSnap ? styles.noSnap : ''} ${paged ? styles.paged : ''} ${isHome ? styles.home : ''}`} onClick={onContentLinkClick}>
      <Routes>
        <Route path="/" element={
          <main className={styles.main}>
            <AppHeader />
            <Scrollbar />
            <Main reference={mainRef} />
            <Advantages reference={advantagesRef} />
            <SliderContainer reference={sliderContainerRef} />
            <HowToInstall reference={howToInstallRef} />
            <LatestNews />
            <Faq />
            <Community />
            <Footer type="main" serverStatus={serverStatus} />
            <SupportChatButton />
          </main>
        } />
        <Route path="/donate" element={
          <React.Suspense fallback={<PublicFallback />}>
            <main className={styles.main}>
              <AppHeader />
              <Donate />
              <Footer type="donate" serverStatus={serverStatus} />
            </main>
          </React.Suspense>
        } />
        <Route path="/rules" element={
          <React.Suspense fallback={<PublicFallback />}>
            <main className={styles.main}>
              <AppHeader />
              <Rules />
              <Footer type="rules" serverStatus={serverStatus} />
            </main>
          </React.Suspense>
        } />
        {/* Скачивание файла из хранилища по прямой ссылке */}
        <Route path="/files/:slug" element={
          <React.Suspense fallback={<PageLoader />}><FileDownloadPage /></React.Suspense>
        } />
        {/* Новости: список и статья — в общем каркасе сайта */}
        <Route path="/news" element={
          <React.Suspense fallback={<PublicFallback />}>
            <main className={styles.main}>
              <AppHeader />
              <NewsGuard><NewsList /></NewsGuard>
              <Footer type="rules" serverStatus={serverStatus} />
            </main>
          </React.Suspense>
        } />
        <Route path="/news/:slug" element={
          <React.Suspense fallback={<PublicFallback />}>
            <main className={styles.main}>
              <AppHeader />
              <NewsGuard><NewsArticle /></NewsGuard>
              <Footer type="rules" serverStatus={serverStatus} />
            </main>
          </React.Suspense>
        } />
        {/* CMS-страницы: отдельная страница или категория/страница —
            в общем каркасе сайта (шапка + футер, как /rules) */}
        <Route path="/pages/:slug" element={
          <React.Suspense fallback={<PublicFallback />}>
            <main className={styles.main}>
              <AppHeader />
              <PublicPage />
              <Footer type="rules" serverStatus={serverStatus} />
            </main>
          </React.Suspense>
        } />
        <Route path="/pages/:catSlug/:pageSlug" element={
          <React.Suspense fallback={<PublicFallback />}>
            <main className={styles.main}>
              <AppHeader />
              <PublicPage />
              <Footer type="rules" serverStatus={serverStatus} />
            </main>
          </React.Suspense>
        } />
        {/* Проверка мониторинга ошибок (Sentry/GlitchTip) */}
        <Route path="/sentry-test" element={
          <React.Suspense fallback={<PageLoader />}><SentryTest /></React.Suspense>
        } />
        {/* OIDC callback (обмен authorization code на токены) */}
        <Route path="/auth/callback" element={
          <React.Suspense fallback={<PageLoader />}><Callback /></React.Suspense>
        } />
        {/* OIDC silent renew (скрытый iframe, prompt=none) */}
        <Route path="/auth/silent" element={
          <React.Suspense fallback={null}><SilentCallback /></React.Suspense>
        } />
        {/* Редиректы для старых URL */}
        <Route path="/register"    element={<Navigate to="/panel/register" replace />} />
        <Route path="/tickets"     element={<Navigate to="/panel/tickets"  replace />} />
        <Route path="/tickets/:id" element={<NavigateTicket />} />
        <Route path="*" element={
          <main className={styles.main}>
            <NotFound />
          </main>
        } />
      </Routes>
    </div>
    <ToastContainer />
    <CookieConsent />
    </ToastProvider>
  );
}

// Хелпер для редиректа /tickets/:id → /panel/tickets/:id
function NavigateTicket() {
  const location = useLocation();
  const id = location.pathname.replace('/tickets/', '');
  return <Navigate to={`/panel/tickets/${id}`} replace />;
}

// Хелпер для редиректа /admin/users/:id → /panel/admin/users/:id
function NavigateAdminUser() {
  const location = useLocation();
  const id = location.pathname.replace('/admin/users/', '');
  return <Navigate to={`/panel/admin/users/${id}`} replace />;
}

export default App;
